<template>
  <div :class="b(modifiers)">
    <div :class="b('head')">
      <div :class="b('title')">
        {{ title }}
      </div>
      <ul
        :class="b('list')"
        role="tablist"
      >
        <template v-for="tab in tabs">
          <li
            v-if="tab.tabId"
            :id="tab.tabId"
            :key="tab.tabId"
            :class="b('list-item', { isSelected: tab.isActive })"
            :aria-controls="tab.tabPanelId ? tab.tabPanelId : null"
            :aria-selected="tab.isActive ? tab.isActive : null"
            :tabindex="tab.isActive ? 0 : -1"
            role="tab"
            @click="onButtonClick(tab)"
          >
            <span
              v-if="tab.slug"
              :id="tab.slug"
              :class="b('anchor')"
            ></span>
            <e-icon-sprite
              icon="i-chevron-1--right"
              :size="design === 'design2' ? '42' : undefined"
            />
            {{ tab.name }}
          </li>
        </template>
      </ul>
    </div>

    <div :class="b('panels')">
      <slot></slot>
    </div>
  </div>
</template>

<script>
  import { provide, reactive } from 'vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import designMixin from '@/mixins/design.js';
  import url from '@/mixins/url.js';

  /**
   * Renders a vertical list of tabs.
   *
   * TODO: storing the active state on the child items is not good practice and should be refactored.
   */
  export default {
    name: 'c-vertical-tabs',
    components: {
      eIconSprite,
    },

    mixins: [url, designMixin],

    props: {
      /**
       * Title text of the component.
       */
      title: {
        type: String,
        default: '',
      },
    },

    setup() {
      const tabsProvider = reactive({
        activeTabId: null,
        tabs: [],
      });

      provide('TabsProvider', tabsProvider);

      return {
        tabsProvider,
      };
    },

    // data() {
    //   return {
    //   };
    // },

    computed: {
      tabs() {
        return this.tabsProvider?.tabs || [];
      },

      /**
       * Sets modifier classes depending on props.
       *
       * @returns   {Object}  modifiers   Modifier BEM classes
       */
      modifiers() {
        return {
          design: this.design,
        };
      },
    },
    // watch: {},
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      if (this.hasHash()) {
        this.handleTabThroughHash();
      }

      const activeTabs = this.tabs?.filter((tab) => tab.isActive);

      if (activeTabs.length > 1) {
        activeTabs.slice(1).forEach((tab) => {
          tab.isActive = false; // Prevents multiple tabs being active.
        });
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Sets tab active.
       *
       * @param {Object} activeTab - The given tab.
       */
      onButtonClick(activeTab) {
        this.tabs.forEach((tab) => {
          tab.isActive = tab.tabId === activeTab.tabId;

          if (tab.isActive) {
            this.tabsProvider.activeTabId = tab.tabId;
          }
        });
      },

      /*
       * Find tab through hash and set it to active
       */
      handleTabThroughHash() {
        const tabThroughHash = this.tabs?.find((tab) => tab.slug === this.getHashValue());

        if (tabThroughHash) {
          this.tabs.forEach((tab) => {
            tab.isActive = false;
          });

          tabThroughHash.isActive = true;
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/v2/variables' as v2_variables;
  @use '@/setup/scss/v2/mixins' as v2_mixins;
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-vertical-tabs {
    $this: &;
    $grid-area--sidebar: sidebar;
    $grid-area--content: content;

    @extend %component-spacing;

    display: grid;
    grid-template-areas:
      '#{$grid-area--sidebar}'
      '#{$grid-area--content}';

    @include mixins.media($down: xs) {
      @include mixins.fullwidth();

      flex-direction: column;
      padding: functions.rem(variables.$spacing--35 variables.$spacing--30);
    }

    @include mixins.media(sm) {
      max-width: 100%;
      margin: 0 auto;
    }

    @include mixins.media(md) {
      grid-template-areas: '#{$grid-area--sidebar} #{$grid-area--content}';
      grid-template-columns: 1fr 65%;
    }

    &__head {
      grid-area: $grid-area--sidebar;
      margin-bottom: variables.$spacing--30;

      @include mixins.media(md) {
        flex: 0 0 30%;
        margin-right: functions.rem(variables.$spacing--100);
        margin-bottom: 0;
      }
    }

    &__title {
      @extend %heading-h2;

      margin-bottom: functions.rem(variables.$spacing--20);
    }

    &__list {
      @extend %list-reset;

      user-select: none;
    }

    &__list-item {
      @include mixins.font(variables.$font-size--16);

      position: relative;
      display: flex;
      justify-content: stretch;
      align-items: center;
      padding: functions.rem(variables.$spacing--20 0);
      border-bottom: 1px solid variables.$color-grayscale--900;
      color: variables.$color-primary--1;
      transition: border-bottom-color variables.$transition-duration--300 ease-in-out;

      @include mixins.media(sm) {
        @include mixins.font(variables.$font-size--18);
      }

      @include mixins.media(md) {
        padding: functions.rem(variables.$spacing--30 0);
      }

      > .e-icon-sprite {
        width: functions.rem(24px);
        height: functions.rem(24px);
        margin-right: functions.rem(variables.$spacing--20);
        opacity: 0;
        color: variables.$color-primary--2;
        transition: opacity variables.$transition-duration--200 ease-in-out;
      }

      &:hover {
        border-bottom-color: variables.$color-primary--2;
        cursor: pointer;
      }

      &--is-selected {
        border-bottom-color: variables.$color-primary--2;

        > .e-icon-sprite {
          opacity: 1;
        }
      }
    }

    &__anchor {
      position: absolute;
      top: calc(var(--header-height) * -1);
      right: 0;
      left: 0;
    }

    &__panels {
      position: relative;
      grid-area: $grid-area--content;
    }

    .c-tab-panel {
      padding-top: 0;
      padding-bottom: 0;
    }

    &--design-design2 {
      #{$this}__head {
        @include mixins.media(md) {
          margin-right: functions.rem(variables.$spacing--45);
        }
      }

      #{$this}__list-item {
        color: v2_variables.$color-grayscale--900;

        > .e-icon-sprite {
          width: functions.rem(42px);
          height: functions.rem(42px);
        }

        &:hover {
          border-bottom-color: variables.$color-grayscale--800;
        }

        &--is-selected {
          @include v2_mixins.gradient-border-ocean-angular();

          border: 0;
          border-bottom: 1px solid variables.$color-primary--1;
          color: variables.$color-primary--1;
          font-weight: variables.$font-weight--semi-bold;
        }
      }
    }
  }
</style>
